import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CoachmarkOverlay } from '@/components/ui/Coachmark';
import { OnboardingCoachmarkProps } from './types';
import { useId } from 'react';
import { X } from 'lucide-react';

const arrowAxisClassNames = {
  horizontal: '-translate-x-1/2',
  vertical: '-translate-y-1/2'
} as const;

const arrowPlacementClassNames = {
  bottom: '-top-3 border-l border-t',
  left: '-right-3 border-r border-t',
  right: '-left-3 border-b border-l',
  top: '-bottom-3 border-b border-r'
} as const;

const widthClassNames = {
  280: 'w-[280px]',
  290: 'w-[290px]'
} as const;

const OnboardingCoachmark = (props: OnboardingCoachmarkProps) => {
  const {
    description,
    isOpen,
    isPrimaryActionDisabled = false,
    isTargetInteractive = false,
    onBack,
    onClose,
    onPrimaryAction,
    onSecondaryAction,
    offset = 24,
    placement,
    primaryActionLabel,
    secondaryActionLabel,
    shouldShowBack,
    stepNumber,
    targetHighlight,
    targetHighlightMode,
    targetId,
    title,
    totalSteps,
    width = 280
  } = props;

  const descriptionId = useId();
  const titleId = useId();
  const arrowAxis = placement === 'bottom' || placement === 'top' ? 'horizontal' : 'vertical';
  const arrowStyle = arrowAxis === 'horizontal'
    ? { left: 'clamp(36px, var(--coachmark-arrow-position, 50%), calc(100% - 36px))' }
    : { top: 'clamp(36px, var(--coachmark-arrow-position, 50%), calc(100% - 36px))' };
  const hasSecondaryAction = Boolean(secondaryActionLabel && onSecondaryAction);
  const resolvedPrimaryActionLabel = primaryActionLabel ?? (stepNumber === totalSteps ? 'Понятно' : 'Далее');
  const resolvedShouldShowBack = shouldShowBack ?? stepNumber > 1;

  return (
    <CoachmarkOverlay
      ariaDescribedBy={descriptionId}
      ariaLabelledBy={titleId}
      isOpen={isOpen}
      isTargetInteractive={isTargetInteractive}
      offset={offset}
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen) onClose();
      }}
      placement={placement}
      shouldCloseOnBackdrop={false}
      targetHighlight={targetHighlight}
      targetHighlightMode={targetHighlightMode}
      targetId={targetId}
      tooltipClassName={widthClassNames[width]}
    >
      <div className="relative w-full p-4 font-sans text-foreground rounded-2xl border border-accent bg-surface shadow-[0_16px_40px_hsl(var(--overlay)/0.24)]">
        <div className="flex items-start gap-2">
          <h2
            className="min-w-0 flex-1 font-sans text-sm font-semibold normal-case leading-5 tracking-normal"
            id={titleId}
          >
            {title}
          </h2>
          <button
            className="-my-1 -mr-1 flex size-7 shrink-0 items-center justify-center text-muted-foreground rounded-lg hover:bg-muted hover:text-foreground"
            aria-label="Закрыть обучалку"
            onClick={onClose}
            type="button"
          >
            <X
              className="size-5"
              aria-hidden="true"
              strokeWidth={1.75}
            />
          </button>
        </div>
        <div
          className="mt-2 text-sm leading-5 text-muted-foreground"
          id={descriptionId}
        >
          {description}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="shrink-0 text-xs leading-4 text-muted-foreground">
            {stepNumber}/{totalSteps}
          </span>
          <div
            className={cn(
              'flex items-center justify-end gap-2',
              hasSecondaryAction ? 'ml-auto shrink-0' : 'min-w-0'
            )}
          >
            {resolvedShouldShowBack && onBack ? (
              <Button
                onClick={onBack}
                size="md"
                type="button"
                variant="secondary"
              >
                Назад
              </Button>
            ) : null}
            {secondaryActionLabel && onSecondaryAction ? (
              <Button
                onClick={onSecondaryAction}
                size="md"
                type="button"
                variant="secondary"
              >
                {secondaryActionLabel}
              </Button>
            ) : null}
            <Button
              disabled={isPrimaryActionDisabled}
              onClick={onPrimaryAction}
              size="md"
              type="button"
            >
              {resolvedPrimaryActionLabel}
            </Button>
          </div>
        </div>
        {placement === 'center' ? null : (
          <span
            aria-hidden="true"
            className={cn(
              'absolute size-6 border-accent bg-surface rotate-45',
              arrowAxisClassNames[arrowAxis],
              arrowPlacementClassNames[placement]
            )}
            style={arrowStyle}
          />
        )}
      </div>
    </CoachmarkOverlay>
  );
};

export default OnboardingCoachmark;